import React from 'react';
import { NavLink } from 'react-router-dom';
import { LuMessageCircle, LuCalendarDays, LuHouse, LuLogIn, LuUserPlus } from 'react-icons/lu';
import UserSearchDropdown from '../UserSearchDropdown';
import NetworkIconButton from '../network/NetworkIconButton';
import MyNetworkDrawer from '../network/MyNetworkDrawer';
import LanguageSwitcher from '../LanguageSwitcher';
import { isAdmin, isParent } from '../../utils/roles';
import NotificationsMenu from './NotificationsMenu';
import FeedbackBell from './FeedbackBell';
import AccountMenu from './AccountMenu';
import ChatUnreadBadge from './ChatUnreadBadge';
import CalendarUnreadBadge from './CalendarUnreadBadge';
import '../../styles/header.css';

export default function AppHeader({
  user,
  loading,
  isAdminPanel,
  isMdDown,
  // Search
  setProfileUser,
  setProfileOpen,
  // Network
  networkOpen,
  setNetworkOpen,
  // Notifications
  notifRef,
  notifAnchorEl,
  notifMenuOpen,
  setNotifAnchorEl,
  setNotifOpen,
  notifCount,
  setNotifCount,
  badgeCount,
  setBadgeCount,
  setBadgeCleared,
  notifications,
  setNotifications,
  dismissedNotifIds,
  setDismissedNotifIds,
  persistDismissedNotifs,
  processingInviteId,
  setProcessingInviteId,
  upcomingItems,
  dismissedKeys,
  setDismissedKeys,
  persistDismissed,
  buildKey,
  UPCOMING_DAYS,
  // Feedback bell
  fbBellRef,
  fbPendingCount,
  fbBellOpen,
  fbPendingLoading,
  setFbBellOpen,
  fetchPendingFb,
  setFbPendingLoading,
  // Account menu
  accountRef,
  accountAnchorEl,
  accountMenuOpen,
  setAccountAnchorEl,
  setAccountOpen,
  getPlanInfo,
  onOpenPricing,
  setThemeOpen,
  handleLogout,
  // Calendar
  calendarBadge,
  // Misc
  navigate,
  showSnack,
  t,
}) {
  const isKid = user && !isAdmin(user);
  const headerClass = `app-header${isKid ? ' kid-header' : ''}`;

  return (
    <header className={headerClass}>
      {/* Search — left side (hidden for parents) */}
      {!isAdminPanel && user && !isParent(user) && (
        <div className="app-header-search">
          <UserSearchDropdown
            placeholder={t('header.inviteFriendTitle', 'Tìm kiếm bạn bè...')}
            onUserSelect={(u) => {
              const mapped = {
                id: u.id || u._id,
                name: u.username || u.name || u.email || 'User',
                avatar: u.avatarUrl || u.avatar || null,
                major: u.major || '—',
                school: u.school || '—',
                bio: u.bio || ''
              };
              setProfileUser(mapped);
              setProfileOpen(true);
            }}
          />
        </div>
      )}

      {/* Icons — right side */}
      <div className="app-header-inner">
        <div className="app-user">
          {!loading && (
            user ? (
              <div className="app-user-row">
                <LanguageSwitcher />

                {!isAdminPanel && !isParent(user) && (
                  <>
                    <NetworkIconButton currentUser={user} onOpen={() => setNetworkOpen(true)} />
                    {!isMdDown && (
                      <NavLink
                        to="/chat"
                        className={({ isActive }) => `header-auth-link${isActive ? ' active' : ''}`}
                        title={t('nav.chat', 'Chat')}
                        aria-label={t('nav.chat', 'Chat')}
                      >
                        <LuMessageCircle />
                        <ChatUnreadBadge />
                      </NavLink>
                    )}
                    <NavLink
                      to="/calendar"
                      className={({ isActive }) => `header-auth-link${isActive ? ' active' : ''}`}
                      title={t('calendar.title', 'Calendar')}
                      aria-label={t('calendar.title', 'Calendar')}
                    >
                      <LuCalendarDays />
                      <CalendarUnreadBadge count={calendarBadge} />
                    </NavLink>
                  </>
                )}

                {/* Notifications bell */}
                {!isAdminPanel && !isParent(user) && (
                  <NotificationsMenu
                    notifRef={notifRef}
                    notifAnchorEl={notifAnchorEl}
                    notifMenuOpen={notifMenuOpen}
                    setNotifAnchorEl={setNotifAnchorEl}
                    setNotifOpen={setNotifOpen}
                    notifCount={notifCount}
                    setNotifCount={setNotifCount}
                    badgeCount={badgeCount}
                    setBadgeCount={setBadgeCount}
                    setBadgeCleared={setBadgeCleared}
                    notifications={notifications}
                    setNotifications={setNotifications}
                    dismissedNotifIds={dismissedNotifIds}
                    setDismissedNotifIds={setDismissedNotifIds}
                    persistDismissedNotifs={persistDismissedNotifs}
                    processingInviteId={processingInviteId}
                    setProcessingInviteId={setProcessingInviteId}
                    upcomingItems={upcomingItems}
                    dismissedKeys={dismissedKeys}
                    setDismissedKeys={setDismissedKeys}
                    persistDismissed={persistDismissed}
                    buildKey={buildKey}
                    UPCOMING_DAYS={UPCOMING_DAYS}
                    navigate={navigate}
                    showSnack={showSnack}
                    t={t}
                  />
                )}

                {/* Admin feedback bell */}
                {isAdmin(user) && (
                  <FeedbackBell
                    fbBellRef={fbBellRef}
                    fbPendingCount={fbPendingCount}
                    fbBellOpen={fbBellOpen}
                    fbPendingLoading={fbPendingLoading}
                    setFbBellOpen={setFbBellOpen}
                    fetchPendingFb={fetchPendingFb}
                    setFbPendingLoading={setFbPendingLoading}
                    t={t}
                  />
                )}

                {/* Account menu */}
                <AccountMenu
                  user={user}
                  accountRef={accountRef}
                  accountAnchorEl={accountAnchorEl}
                  accountMenuOpen={accountMenuOpen}
                  setAccountAnchorEl={setAccountAnchorEl}
                  setAccountOpen={setAccountOpen}
                  isAdminPanel={isAdminPanel}
                  getPlanInfo={getPlanInfo}
                  onOpenPricing={onOpenPricing}
                  setThemeOpen={setThemeOpen}
                  handleLogout={handleLogout}
                  t={t}
                />
              </div>
            ) : (
              <div className="app-user-links">
                <LanguageSwitcher />
                <NavLink
                  to="/"
                  className={({ isActive }) => `header-auth-link${isActive ? ' active' : ''}`}
                  title={t('nav.home', 'Home')}
                  aria-label={t('nav.home', 'Home')}
                  end
                >
                  <LuHouse />
                  <span className="sr-only">{t('nav.home', 'Home')}</span>
                </NavLink>
                <NavLink
                  to="/login"
                  className={({ isActive }) => `header-auth-link${isActive ? ' active' : ''}`}
                  title={t('common.login')}
                  aria-label={t('common.login')}
                >
                  <LuLogIn />
                  <span className="sr-only">{t('common.login')}</span>
                </NavLink>
                <NavLink
                  to="/register"
                  className={({ isActive }) => `header-auth-link${isActive ? ' active' : ''}`}
                  title={t('common.register')}
                  aria-label={t('common.register')}
                >
                  <LuUserPlus />
                  <span className="sr-only">{t('common.register')}</span>
                </NavLink>
              </div>
            )
          )}
        </div>
      </div>

      <MyNetworkDrawer open={networkOpen} onClose={() => setNetworkOpen(false)} currentUser={user} />
    </header>
  );
}
